import type { FC, ReactNode } from 'react'
import type { ServiceContent } from '@/constants/site'
import type { ServiceKey } from '@/types'

import { useRef } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { gsap, MOTION, useGSAP } from '@/lib/motion'
import { QuoteLink } from '@/components/generals/quote-link'
import { SplitWords } from '@/components/generals/split-words'
import { PrintVisual, RankVisual, WebVisual } from '@/components/landing/service-visuals'
import { SERVICES } from '@/constants/site'

const PANEL_STYLE: Record<ServiceKey, { panel: string, surface: 'dark' | 'light', button: string, muted: string }> = {
    'web-design': {
        panel: 'bg-ink-soft text-paper',
        surface: 'dark',
        button: 'bg-signal text-ink hover:bg-paper',
        muted: 'text-haze'
    },
    seo: {
        panel: 'bg-graphite text-paper',
        surface: 'dark',
        button: 'bg-signal text-ink hover:bg-paper',
        muted: 'text-frost'
    },
    print: {
        panel: 'bg-frost text-ink',
        surface: 'light',
        button: 'bg-ink text-paper hover:bg-signal hover:text-ink',
        muted: 'text-stone'
    }
}

const VISUALS: Record<ServiceKey, FC> = {
    'web-design': WebVisual,
    seo: RankVisual,
    print: PrintVisual
}

interface ServicePanelProps {
    service: ServiceContent
}

const ServicePanel: FC<ServicePanelProps> = ({ service }): ReactNode => {
    const style = PANEL_STYLE[service.key]
    const Visual = VISUALS[service.key]

    return (
        <>
            <div data-panel-marker='' aria-hidden='true' className='h-0' />

            <article
                data-panel={service.key}
                data-surface={style.surface}
                aria-labelledby={`service-${service.key}`}
                className={cn('relative overflow-clip lg:h-svh', style.panel)}
            >
                <div
                    data-panel-inner=''
                    className='relative grid h-full grid-cols-1 content-center gap-x-10 gap-y-14 px-(--gutter) pb-20 pt-24 lg:grid-cols-12 lg:pb-[6vh] lg:pt-[calc(var(--header-h)+3vh)]'
                >
                    <div className='flex flex-col lg:col-span-6'>
                        <p className={cn('label flex items-center gap-4', style.muted)}>
                            <span>{service.index} / 03</span>
                            <span aria-hidden='true' className='h-px w-10 bg-current opacity-50' />
                            <span>{service.label}</span>
                        </p>

                        <h3
                            id={`service-${service.key}`}
                            data-service-title=''
                            className='display mt-7 text-[clamp(3rem,7.2vw,8.75rem)] leading-[0.86] wdth-75'
                        >
                            {service.title.map(line => (
                                <span key={line} className='block'>
                                    <span className='line-mask'>
                                        <span data-title-line=''>{line}</span>
                                    </span>
                                </span>
                            ))}
                        </h3>

                        {service.status && (
                            <p className='label mt-7 inline-flex w-fit items-center gap-2.5 border border-frost/40 px-3 py-2 text-frost'>
                                <span aria-hidden='true' className='size-1.5 rounded-full bg-signal' />
                                {service.status}
                            </p>
                        )}

                        <p data-service-copy='' className='mt-7 max-w-[34ch] text-lg leading-relaxed md:text-xl'>
                            {service.description}
                        </p>

                        <QuoteLink
                            service={service.key}
                            className={cn(
                                'group mt-9 inline-flex h-14 w-fit items-center gap-3 px-7 font-semibold transition-colors duration-300',
                                style.button
                            )}
                        >
                            {service.cta}
                            <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:translate-x-1' />
                        </QuoteLink>
                    </div>

                    <div className='flex items-center lg:col-span-6'>
                        <Visual />
                    </div>
                </div>

                <div data-panel-dim='' aria-hidden='true' className='pointer-events-none absolute inset-0 bg-ink opacity-0' />
            </article>
        </>
    )
}

/** Chapter five: three services, stacked like sheets that slide over each other. */
export const ServicesSection: FC = (): ReactNode => {
    const ref = useRef<HTMLElement>(null)

    useGSAP(() => {
        const section = ref.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const panels = q('[data-panel]') as Array<HTMLElement>
        const markers = q('[data-panel-marker]') as Array<HTMLElement>
        const mm = gsap.matchMedia()

        mm.add({ ok: MOTION.ok, reduce: MOTION.reduce, desktop: MOTION.desktop }, context => {
            const { ok, desktop } = context.conditions as { ok: boolean, reduce: boolean, desktop: boolean }
            const web = q('[data-visual="web"]')[0] as HTMLElement | undefined
            const rank = q('[data-visual="rank"]')[0] as HTMLElement | undefined
            const print = q('[data-visual="print"]')[0] as HTMLElement | undefined
            const you = rank?.querySelector('[data-rank-you]')
            const rows = rank ? Array.from(rank.querySelectorAll<HTMLElement>('[data-rank-row]')) : []

            const placeRank = (position: number) => {
                if (you) gsap.set(you, { yPercent: -(6 - position) * 100 })
                rows.forEach((row, index) => gsap.set(row, { yPercent: gsap.utils.clamp(0, 1, index + 1 - position) * 100 }))
            }

            if (!ok) {
                placeRank(0)

                return
            }

            gsap.fromTo(q('[data-services-title] [data-word]'), { yPercent: 110 }, {
                yPercent: 0,
                duration: 1.2,
                stagger: 0.06,
                ease: 'expo.out',
                scrollTrigger: { trigger: q('[data-services-title]')[0], start: 'top 85%', toggleActions: 'play none none reverse' }
            })

            if (desktop) {
                gsap.set(panels, { position: 'sticky', top: 0, marginBottom: '55vh' })

                panels.forEach((panel, index) => {
                    const next = markers[index + 1]

                    if (!next) return

                    gsap.timeline({
                        scrollTrigger: { trigger: next, start: 'top bottom', end: 'top top', scrub: true }
                    })
                        .to(panel.querySelector('[data-panel-inner]'), { scale: 0.9, yPercent: -3, ease: 'none' }, 0)
                        .to(panel.querySelector('[data-panel-dim]'), { opacity: 0.7, ease: 'none' }, 0)
                })
            }

            const markerOf = (panel: HTMLElement) => markers[panels.indexOf(panel)] ?? panel

            const visualRange = (panel: HTMLElement) => desktop
                ? { trigger: markerOf(panel), start: 'top 75%', end: '+=125%', scrub: 0.5 }
                : { trigger: panel, start: 'top 80%', end: 'bottom 35%', scrub: 0.5 }

            panels.forEach(panel => {
                gsap.fromTo(panel.querySelectorAll('[data-title-line]'), { yPercent: 110 }, {
                    yPercent: 0,
                    duration: 1.1,
                    stagger: 0.08,
                    ease: 'expo.out',
                    scrollTrigger: { trigger: markerOf(panel), start: 'top 60%', toggleActions: 'play none none reverse' }
                })
            })

            const webPanel = web?.closest<HTMLElement>('[data-panel]')

            if (web && webPanel) {
                const timeline = gsap.timeline({ defaults: { ease: 'none' }, scrollTrigger: visualRange(webPanel) })

                web.querySelectorAll<HTMLElement>('[data-shot]').forEach((shot, index) => {
                    timeline.fromTo(shot, { objectPosition: '50% 0%' }, { objectPosition: '50% 100%', duration: 1 }, index * 0.08)
                })

                timeline.fromTo(web.querySelector('[data-badge]'), { yPercent: 60, autoAlpha: 0 }, { yPercent: 0, autoAlpha: 1, duration: 0.25, ease: 'power2.out' }, 0.1)
            }

            const rankPanel = rank?.closest<HTMLElement>('[data-panel]')

            if (rank && rankPanel) {
                const state = { position: 6 }

                placeRank(6)
                gsap.to(state, {
                    position: 0,
                    ease: 'power1.inOut',
                    scrollTrigger: visualRange(rankPanel),
                    onUpdate: () => placeRank(state.position)
                })
            }

            const printPanel = print?.closest<HTMLElement>('[data-panel]')

            if (print && printPanel) {
                gsap.timeline({ defaults: { ease: 'power2.inOut' }, scrollTrigger: visualRange(printPanel) })
                    .fromTo(print.querySelector('[data-fold="left"]'), { rotateY: 168 }, { rotateY: 0, duration: 0.5 }, 0)
                    .fromTo(print.querySelector('[data-fold="right"]'), { rotateY: -172 }, { rotateY: 0, duration: 0.5 }, 0.32)
                    .fromTo(print.querySelectorAll('[data-fold-shade]'), { opacity: 0.45 }, { opacity: 0, duration: 0.5, stagger: 0.32 }, 0)
                    .fromTo(print.querySelector('[data-crop]'), { scale: 1.2, autoAlpha: 0 }, { scale: 1, autoAlpha: 1, duration: 0.2 }, 0.78)
                    .fromTo(print.querySelector('[data-reg]'), { rotate: -90, autoAlpha: 0 }, { rotate: 0, autoAlpha: 1, duration: 0.2 }, 0.8)
                    .fromTo(print.querySelectorAll('[data-tint]'), { scaleY: 0 }, { scaleY: 1, duration: 0.15, stagger: 0.02 }, 0.85)
            }
        })

        return () => mm.revert()
    }, { scope: ref })

    return (
        <section
            ref={ref}
            id='services'
            data-tone='ink'
            data-surface='dark'
            aria-labelledby='services-title'
            className='relative bg-ink text-paper'
        >
            <div className='grid items-end gap-x-8 gap-y-6 px-(--gutter) pb-[10vh] pt-[18vh] md:grid-cols-12'>
                <p className='label text-haze md:col-span-3 md:pb-4'>
                    <span className='mr-3'>03</span>
                    What we offer
                </p>

                <h2
                    id='services-title'
                    data-services-title=''
                    className='display text-[clamp(3.4rem,11vw,13.5rem)] leading-[0.82] wdth-75 md:col-span-9'
                >
                    <SplitWords text="Our services" />
                </h2>
            </div>

            <div data-stack='' className='relative'>
                {SERVICES.map(service => (
                    <ServicePanel key={service.key} service={service} />
                ))}
            </div>
        </section>
    )
}
